import { getAuthToken } from './cookieUtils';

// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Types for task objects
interface DockObject {
  uuid: string;
  name: string;
  status?: string;
}

interface YardSpotObject {
  uuid: string;
  name: string;
  facilityZoneUuid?: string;
}

interface TrailerObject {
  uuid: string;
  number: string;
}

interface ExternalLocationObject {
  name: string;
}

interface FacilityZoneObject {
  uuid: string;
  name: string;
}

interface TaskStatus {
  uuid: string;
  status: string;
}

interface Task {
  uuid: string;
  task_status: TaskStatus | null;
  from_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
  to_object: DockObject | YardSpotObject | TrailerObject | ExternalLocationObject | FacilityZoneObject | null;
}

// Active task statuses that should lock locations
const ACTIVE_TASK_STATUSES = [
  'created',
  'assigned',
  'in_progress',
  'pending',
  'active'
];

// Helper function to check if an object is a YardSpotObject
const isYardSpotObject = (obj: any): obj is YardSpotObject => {
  return obj && typeof obj === 'object' && 'uuid' in obj && 'name' in obj && !('status' in obj);
};

// Helper function to check if an object is a DockObject
const isDockObject = (obj: any): obj is DockObject => {
  return obj && typeof obj === 'object' && 'uuid' in obj && 'name' in obj && ('status' in obj || obj.__typename === 'DockObject');
};

// Function to fetch active tasks and determine locked locations
export const fetchTaskBasedLocks = async (facilityId: string): Promise<{
  lockedYardSpots: Set<string>;
  lockedDockDoors: Set<string>;
}> => {
  try {
    // Get auth token from cookie
    const authToken = getAuthToken();

    // GraphQL query for active tasks by facility
    const tasksQuery = `
      query GetActiveTasksByFacility($facilityId: ID!) {
        tasks_by_facility(facility_id: $facilityId) {
          tasks {
            uuid
            task_status {
              uuid
              status
            }
            from_object {
              ... on DockObject {

                uuid
                name
              }
              ... on YardSpotObject {

                uuid
                name
                facilityZoneUuid
              }
              ... on TrailerObject {
                __typename                
                uuid
                number
              }
              ... on ExternalLocationObject {
                __typename
                name
              }
              ... on FacilityZoneObject {
                __typename
                uuid
                name
              }
            }
            to_object {
              ... on YardSpotObject {
                __typename
                uuid
                name
                facilityZoneUuid
              }
              ... on DockObject {
                __typename
                uuid
                name
              }
              ... on WarehouseZoneObject {
                __typename
                uuid
                name
              }
              ... on TrailerObject {
                __typename
                uuid
                number
              }
              ... on ExternalLocationObject {
                __typename
                name
              }
              ... on FacilityZoneObject {
                __typename
                uuid
                name
              }
            }
          }
        }
      }
    `;

    // Execute the GraphQL query
    const response = await fetch(GRAPHQL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        query: tasksQuery,
        variables: {
          facilityId: facilityId
        }
      })
    });

    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return {
        lockedYardSpots: new Set(),
        lockedDockDoors: new Set()
      };
    }

    const tasks: Task[] = result.data?.tasks_by_facility?.tasks || [];
    
    // Filter for active tasks only
    const activeTasks = tasks.filter(task => 
      task.task_status && 
      ACTIVE_TASK_STATUSES.includes(task.task_status.status.toLowerCase())
    );

    console.log(`Found ${activeTasks.length} active tasks out of ${tasks.length} total tasks`);

    const lockedYardSpots = new Set<string>();
    const lockedDockDoors = new Set<string>();

    // Process each active task to find locked locations
    activeTasks.forEach(task => {
      // Check from_object
      if (task.from_object) {
        if (isYardSpotObject(task.from_object)) {
          lockedYardSpots.add(task.from_object.uuid);
          console.log(`Locking yard spot ${task.from_object.name} (${task.from_object.uuid}) from task ${task.uuid}`);
        } else if (isDockObject(task.from_object)) {
          lockedDockDoors.add(task.from_object.uuid);
          console.log(`Locking dock door ${task.from_object.name} (${task.from_object.uuid}) from task ${task.uuid}`);
        }
      }

      // Check to_object
      if (task.to_object) {
        if (isYardSpotObject(task.to_object)) {
          lockedYardSpots.add(task.to_object.uuid);
          console.log(`Locking yard spot ${task.to_object.name} (${task.to_object.uuid}) from task ${task.uuid}`);
        } else if (isDockObject(task.to_object)) {
          lockedDockDoors.add(task.to_object.uuid);
          console.log(`Locking dock door ${task.to_object.name} (${task.to_object.uuid}) from task ${task.uuid}`);
        }
      }
    });

    console.log(`Task-based locks: ${lockedYardSpots.size} yard spots, ${lockedDockDoors.size} dock doors`);

    return {
      lockedYardSpots,
      lockedDockDoors
    };

  } catch (error) {
    console.error('Error fetching task-based locks:', error);
    return {
      lockedYardSpots: new Set(),
      lockedDockDoors: new Set()
    };
  }
};

// Function to merge task-based locks with manual locks
export const mergeLockSets = (
  taskBasedLocks: Set<string>,
  manualLocks: Set<string>
): Set<string> => {
  const merged = new Set<string>();
  taskBasedLocks.forEach(id => merged.add(id));
  manualLocks.forEach(id => merged.add(id));
  return merged;
}; 